<template>
    <div
        ref="menuRoot"
        class="pip-login-bar d-flex flex-column justify-content-center dropdown"
    >
        <div
            class="dropdown-toggle"
            role="button"
            tabindex="0"
            aria-haspopup="true"
            :aria-expanded="isOpen ? 'true' : 'false'"
            @click="toggleMenu"
            @keydown.enter.prevent="toggleMenu"
            @keydown.space.prevent="toggleMenu"
            @keydown.esc.prevent="closeMenu"
        >
            <div v-if="isLoggedIn" class="px-3">
                <div class="welcome">
                    <small class="d-block">Welcome</small>
                    <span>
                        {{ user.f_name }} {{ user.l_name }}
                        <i
                            class="account-arrow fas fa-chevron-down"
                            :class="{ 'is-open': isOpen }"
                        ></i>
                    </span>
                </div>
            </div>
            <div v-else class="px-5">
                <div class="login">Distributor Log in</div>
            </div>
        </div>

        <div class="dropdown-menu" :class="{ 'is-open': isOpen }">
            <div v-if="isLoggedIn" class="dashboard-menu">
                <ul>
                    <li id="account">
                        <router-link :to="{ name: 'myAccount' }">
                            My Account
                        </router-link>
                        <a
                            id="logOut"
                            href="javascript:void(0);"
                            @click.prevent="logout"
                        >
                            Log Out
                        </a>
                    </li>
                    <li v-if="user.user_group_id == 8" id="userlist">
                        <router-link :to="{ name: 'verify-user' }">
                            Verify User
                        </router-link>
                    </li>
                </ul>

                <small class="menu-subheading">EMPLOYEES only</small>
                <ul class="pb-2">
                    <li>
                        <router-link :to="{ name: 'myTools' }">
                            My Tools
                        </router-link>
                    </li>
                    <li>
                        <a
                            href="https://form.asana.com/?k=53A02TdaaoHj3o9ukhYpew&d=1202255696728322"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Creative Request Form
                        </a>
                    </li>
                </ul>

                <small class="menu-subheading">portals</small>
                <ul class="pb-2">
                    <li>
                        <router-link :to="{ name: 'SpecSheetDownload' }">
                            Spec Sheet Portal
                        </router-link>
                    </li>
                    <li v-if="hasContentPortalAccess">
                        <router-link :to="{ name: 'literature-portal' }">
                            Literature Portal
                        </router-link>
                    </li>
                    <li v-if="hasContentPortalAccess">
                        <router-link :to="{ name: 'video-portal' }">
                            Video Portal
                        </router-link>
                    </li>
                </ul>

                <template v-if="hasPricingAccess">
                    <small class="menu-subheading">pricing</small>
                    <ul class="pb-2">
                        <li>
                            <router-link :to="{ name: 'DynamicPriceList' }">
                                Dynamic Price List
                            </router-link>
                        </li>
                        <li>
                            <router-link :to="{ name: 'PriceListPortal' }">
                                Price List Portal
                            </router-link>
                        </li>
                        <li v-if="hasPricingWorkflowAccess">
                            <router-link
                                :to="{ name: 'PriceWorkflowSubmission' }"
                            >
                                Pricing Activity Workflow
                            </router-link>
                        </li>
                    </ul>
                </template>

                <template v-if="hasResourceAccess">
                    <small class="menu-subheading">resources</small>
                    <ul class="pb-2">
                        <li>
                            <a
                                :href="richProductFileURL"
                                download="PIP-Retail-Product-Information-2026-10.xlsx"
                            >
                                Rich Product Detail - October 2026
                            </a>
                        </li>
                    </ul>
                </template>

                <ul class="pb-2">
                    <li>
                        <router-link :to="{ name: 'ImageGallery' }">
                            Download Product Images
                        </router-link>
                    </li>
                </ul>
            </div>

            <div v-else class="login-block">
                <div class="text-center mb-3">
                    <img
                        src="/images/west-chester-logo.svg"
                        alt="logo"
                        class="d-inline-block mb-2"
                    />
                    <span class="d-block text-center">DISTRIBUTOR TOOLS</span>
                </div>
                <div>
                    <small class="d-block">Don't have an account?</small>
                    <small class="mb-4 d-inline-block">
                        <router-link :to="{ name: 'login' }" class="text-light">
                            Click here to Sign Up
                        </router-link>
                    </small>
                </div>
                <form @submit.prevent="login">
                    <div class="form-group">
                        <label for="desktop-nav-email">Email Address:</label>
                        <input
                            id="desktop-nav-email"
                            v-model="email"
                            type="text"
                            name="email"
                            class="form-control"
                        />
                    </div>
                    <div class="form-group mb-1">
                        <label for="desktop-nav-password">Password:</label>
                        <input
                            id="desktop-nav-password"
                            v-model="password"
                            type="password"
                            name="password"
                            class="form-control"
                        />
                    </div>
                    <small class="d-block text-right">
                        <router-link
                            :to="{ name: 'reset-password' }"
                            class="text-light"
                        >
                            Did you forget your password?
                        </router-link>
                    </small>

                    <button
                        class="btn-round-white pt9rem mt-4"
                        type="submit"
                        :disabled="loading"
                    >
                        Login
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import { logIn } from "../utils/auth";

const { proxy } = getCurrentInstance();
const store = proxy.$store;

const email = ref(null);
const isOpen = ref(false);
const loading = ref(false);
const menuRoot = ref(null);
const password = ref(null);

const isLoggedIn = computed(() => store.state.isLoggedIn);
const priceDeviationAdmins = computed(
    () => store.state.priceDeviationAdmins || []
);
const user = computed(() => store.state.user || {});
const userEmail = computed(() => store.state.userEmail);

const pricingAccessUserGroups = [2, 8, 9, 10];
const richProductFileURL =
    "/archive/products/info/PIP-Retail-Product-Information-2026-10.xlsx";

const hasPricingAccess = computed(() =>
    pricingAccessUserGroups.includes(user.value.user_group_id)
);
const hasPricingWorkflowAccess = computed(
    () =>
        priceDeviationAdmins.value.includes(user.value.id) ||
        user.value.user_group_id == 9 ||
        user.value.user_group_id == 10
);
const hasResourceAccess = computed(
    () => ![0, 1, 7].includes(user.value.user_group_id)
);
const hasContentPortalAccess = computed(
    () => user.value.approved === "y" && ![0, 1].includes(Number(user.value.user_group_id))
);

function closeMenu() {
    isOpen.value = false;
}

function toggleMenu() {
    isOpen.value = !isOpen.value;
}

function handleDocumentClick(event) {
    if (
        isOpen.value &&
        menuRoot.value &&
        !menuRoot.value.contains(event.target)
    ) {
        closeMenu();
    }
}

function handleDocumentKeydown(event) {
    if (event.key === "Escape") closeMenu();
}

async function login() {
    loading.value = true;

    try {
        await window.axios.get("/sanctum/csrf-cookie");
        const response = await window.axios.post("/login", {
            email: email.value,
            password: password.value,
        });

        if (response.data.success) {
            logIn(
                response.data.user.email,
                response.data.user.approved,
                response.data.user.user_group_id
            );
            window.axios.get(
                `/api/user/update/login/${response.data.user.id}`
            );
            await store.dispatch("loadUser");

            await proxy.$router.push(
                proxy.$route.query.from
                    ? { name: proxy.$route.query.from }
                    : { name: "home" }
            );
            return;
        }

        const queryByMessage = {
            "Not Verified": { verify: "false" },
            "No Access": { access: "false" },
        };
        const query = queryByMessage[response.data.msg];
        if (query) {
            await proxy.$router.push({ name: "login", query });
        }
    } catch (error) {
        await proxy.$router.push({
            name: "login",
            query: { login: "false" },
        });
    } finally {
        loading.value = false;
    }
}

async function logout() {
    try {
        await window.axios.post("/logout");
    } finally {
        await store.dispatch("logout");
        if (proxy.$route.name !== "home") {
            await proxy.$router.push({ name: "home" });
        }
    }
}

watch(() => proxy.$route.fullPath, closeMenu);

onMounted(() => {
    document.addEventListener("click", handleDocumentClick);
    document.addEventListener("keydown", handleDocumentKeydown);

    if (!isLoggedIn.value || !userEmail.value) return;

    window.axios
        .get(`/api/getUserByEmail/${userEmail.value}`)
        .then((response) => store.commit("setUser", response.data));
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleDocumentClick);
    document.removeEventListener("keydown", handleDocumentKeydown);
});
</script>
<style scoped>
.dropdown-menu {
    background: transparent !important;
    display: block;
    left: auto;
    margin: 0 !important;
    overflow: hidden;
    right: 0;
    top: 100%;
    transform-origin: top center;
    padding: 0 !important;
    width: 100%;
    border-top-right-radius: 0px !important;
    border-top-left-radius: 0px !important;
    will-change: clip-path, opacity, transform;
    clip-path: inset(0 0 100% 0);
    opacity: 0;
    pointer-events: none;
    transform: translateY(-4px);
    visibility: hidden;
    transition: clip-path 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease,
        transform 320ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 320ms;
}

.dropdown-menu.is-open {
    clip-path: inset(0 0 0 0);
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
    visibility: visible;
    transition-delay: 0s;
}

.account-arrow {
    align-items: center;
    display: inline-flex;
    height: 1em;
    justify-content: center;
    line-height: 1;
    margin-left: 0.5rem;
    transform: rotate(0deg);
    transform-origin: 50% 50%;
    transition: transform 280ms cubic-bezier(0.4, 0, 0.2, 1);
    width: 1em;
}

.account-arrow.is-open {
    transform: rotate(180deg);
}
</style>
